import React, { useState } from 'react';
import { getDailyReport, exportClients, exportDailyReport, exportLeads, exportRejectedPayments, exportInventory, exportControlMensual } from '../lib/api';
import { getStoredToken } from '../lib/authStorage';
import { formatCurrency, getTodayDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  FileText, 
  Download,
  Loader2,
  Calendar,
  Users,
  UserPlus,
  CreditCard,
  Package,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Copy,
  CheckCircle,
  Edit,
  ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';

const Reportes = () => {
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [dailyReport, setDailyReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editableText, setEditableText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [cmExportMonth, setCmExportMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const buildReportText = (report) => {
    return `REPORTE DIARIO GRIT STUDIO
Fecha: ${report.date}
Responsable: ${report.responsible}

CAJA
- Efectivo inicial: ${formatCurrency(report.opening_cash)}
- Ingreso efectivo: ${formatCurrency(report.cash_income)}
- Ingreso tarjeta: ${formatCurrency(report.card_income)}
- Ingreso transferencia: ${formatCurrency(report.transfer_income)}
- Total ingresos: ${formatCurrency(report.total_income)}
- Gastos: ${formatCurrency(report.expenses)}
- Efectivo final: ${report.closing_cash ? formatCurrency(report.closing_cash) : 'Pendiente'}
- Diferencia: ${formatCurrency(report.difference)}

CLIENTES
- Nuevos: ${report.new_clients}
- Cancelaciones: ${report.cancellations}
- Pagos recuperados: ${report.recovered_payments}${report.observations ? `\n\nObservaciones: ${report.observations}` : ''}`;
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const response = await getDailyReport(selectedDate);
      setDailyReport(response.data);
      setEditableText(buildReportText(response.data));
      setIsEditing(false);
    } catch (error) {
      toast.error('Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type, fechaDesde, fechaHasta) => {
    const token = getStoredToken();
    let url;
    
    switch (type) {
      case 'clients':
        url = exportClients();
        break;
      case 'daily':
        url = exportDailyReport(selectedDate);
        break;
      case 'leads':
        url = exportLeads();
        break;
      case 'payments':
        url = exportRejectedPayments();
        break;
      case 'inventory':
        url = exportInventory();
        break;
      case 'control-mensual':
        url = exportControlMensual(fechaDesde, fechaHasta);
        break;
      default:
        return;
    }
    
    try {
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) {
        if (response.status === 403) {
          toast.error('Solo Dirección puede descargar reportes');
          return;
        }
        throw new Error('Error en descarga');
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      const disposition = response.headers.get('content-disposition');
      a.download = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') || `reporte_${type}.csv` : `reporte_${type}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Descarga completada');
    } catch (error) {
      
      toast.error('Error al descargar reporte');
    }
  };

  const copyReportToClipboard = () => {
    if (!editableText) return;
    navigator.clipboard.writeText(editableText);
    setCopied(true);
    toast.success('Reporte copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportControlMensual = () => {
    const [y, m] = cmExportMonth.split('-');
    const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
    handleExport('control-mensual', `${cmExportMonth}-01`, `${cmExportMonth}-${lastDay}`);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reportes-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Reportes</h1>
        <p className="page-subtitle">Generación y exportación de reportes</p>
      </div>

      {/* Export Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="export-cards">
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#F5F0E8] rounded-lg">
              <Users className="text-[#2B4257]" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-[#1A2835]">Clientes</h3>
              <p className="text-sm text-[#5C7083]">Lista completa de clientes</p>
            </div>
          </div>
          <Button 
            className="w-full btn-secondary gap-2"
            onClick={() => handleExport('clients')}
            data-testid="btn-export-clients"
          >
            <Download size={16} />
            Exportar Excel
          </Button>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#FAF0EC] rounded-lg">
              <UserPlus className="text-[#D98A6C]" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-[#1A2835]">Leads</h3>
              <p className="text-sm text-[#5C7083]">Prospectos y seguimiento</p>
            </div>
          </div>
          <Button 
            className="w-full btn-secondary gap-2"
            onClick={() => handleExport('leads')}
            data-testid="btn-export-leads"
          >
            <Download size={16} />
            Exportar Excel
          </Button>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#FBEAE9] rounded-lg">
              <CreditCard className="text-[#C25953]" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-[#1A2835]">Pagos Rechazados</h3>
              <p className="text-sm text-[#5C7083]">Cobros rechazados</p>
            </div>
          </div>
          <Button 
            className="w-full btn-secondary gap-2"
            onClick={() => handleExport('payments')}
            data-testid="btn-export-payments"
          >
            <Download size={16} />
            Exportar Excel
          </Button>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#EDF5F0] rounded-lg">
              <Package className="text-[#4A7C59]" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-[#1A2835]">Inventario</h3>
              <p className="text-sm text-[#5C7083]">Productos y stock</p>
            </div>
          </div>
          <Button 
            className="w-full btn-secondary gap-2"
            onClick={() => handleExport('inventory')}
            data-testid="btn-export-inventory"
          >
            <Download size={16} />
            Exportar Excel
          </Button>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#F5F0E8] rounded-lg">
              <ClipboardList className="text-[#2B4257]" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-[#1A2835]">Control Mensual</h3>
              <p className="text-sm text-[#5C7083]">Casos operativos del mes</p>
            </div>
          </div>
          <div className="flex gap-2 mb-3">
            <Input type="month" value={cmExportMonth} onChange={(e) => setCmExportMonth(e.target.value)}
              className="flex-1 text-sm" data-testid="input-cm-export-month" />
          </div>
          <Button 
            className="w-full btn-secondary gap-2"
            onClick={handleExportControlMensual}
            data-testid="btn-export-control-mensual"
          >
            <Download size={16} />
            Exportar Excel
          </Button>
        </div>
      </div>

      {/* Daily Report Generator */}
      <div className="card" data-testid="daily-report-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-[#2B4257] rounded-lg">
            <FileText className="text-white" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-[#1A2835]">Reporte Diario</h3>
            <p className="text-sm text-[#5C7083]">Genera el resumen del día para compartir</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Label className="form-label">Fecha del Reporte</Label>
            <div className="flex gap-2">
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="form-input"
                data-testid="input-report-date"
              />
              <Button 
                className="btn-primary gap-2"
                onClick={handleGenerateReport}
                disabled={loading}
                data-testid="btn-generate-report"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar size={16} />}
                Generar
              </Button>
            </div>
          </div>
        </div>

        {/* Generated Report */}
        {dailyReport && (
          <div className="space-y-4 animate-fade-in" data-testid="daily-report-content">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-[#1A2835]">
                Reporte del {dailyReport.date}
              </h4>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  className="btn-secondary gap-2"
                  onClick={() => setIsEditing(!isEditing)}
                  data-testid="btn-edit-report"
                >
                  <Edit size={16} />
                  {isEditing ? 'Vista previa' : 'Editar texto'}
                </Button>
                <Button 
                  variant="outline"
                  className="btn-secondary gap-2"
                  onClick={copyReportToClipboard}
                  data-testid="btn-copy-report"
                >
                  {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                  {copied ? 'Copiado' : 'Copiar'}
                </Button>
                <Button 
                  className="btn-primary gap-2"
                  onClick={() => handleExport('daily')}
                  data-testid="btn-export-daily"
                >
                  <Download size={16} />
                  Excel
                </Button>
              </div>
            </div>

            {/* Editable Text Mode */}
            {isEditing ? (
              <div className="space-y-2" data-testid="editable-report">
                <p className="text-xs text-[#5C7083]">Edita el texto del reporte antes de copiar. Los cambios solo afectan al texto copiado.</p>
                <textarea
                  value={editableText}
                  onChange={(e) => setEditableText(e.target.value)}
                  className="form-input w-full min-h-[300px] font-mono text-sm resize-y"
                  data-testid="textarea-editable-report"
                />
              </div>
            ) : (
              <>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Cash Summary */}
              <div className="p-4 bg-[#F5F0E8] rounded-lg">
                <h5 className="font-semibold text-[#1A2835] mb-3 flex items-center gap-2">
                  <DollarSign size={18} />
                  Caja
                </h5>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#5C7083]">Efectivo inicial</span>
                    <span className="font-medium">{formatCurrency(dailyReport.opening_cash)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5C7083]">Ingreso efectivo</span>
                    <span className="font-medium text-[#4A7C59]">{formatCurrency(dailyReport.cash_income)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5C7083]">Ingreso tarjeta</span>
                    <span className="font-medium text-[#4A7C59]">{formatCurrency(dailyReport.card_income)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5C7083]">Ingreso transferencia</span>
                    <span className="font-medium text-[#4A7C59]">{formatCurrency(dailyReport.transfer_income)}</span>
                  </div>
                  <div className="flex justify-between border-t border-[#E8E2D7] pt-2 mt-2">
                    <span className="text-[#5C7083]">Total ingresos</span>
                    <span className="font-semibold text-[#4A7C59]">{formatCurrency(dailyReport.total_income)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5C7083]">Gastos</span>
                    <span className="font-medium text-[#C25953]">{formatCurrency(dailyReport.expenses)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5C7083]">Efectivo final</span>
                    <span className="font-medium">
                      {dailyReport.closing_cash !== null ? formatCurrency(dailyReport.closing_cash) : 'Pendiente'}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-[#E8E2D7] pt-2 mt-2">
                    <span className="text-[#5C7083]">Diferencia</span>
                    <span className={`font-semibold ${
                      dailyReport.difference === 0 ? 'text-[#4A7C59]' :
                      dailyReport.difference > 0 ? 'text-[#D98A6C]' : 'text-[#C25953]'
                    }`}>
                      {formatCurrency(dailyReport.difference)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Clients Summary */}
              <div className="p-4 bg-[#F5F0E8] rounded-lg">
                <h5 className="font-semibold text-[#1A2835] mb-3 flex items-center gap-2">
                  <Users size={18} />
                  Clientes
                </h5>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="text-[#4A7C59]" size={16} />
                      <span className="text-sm">Nuevos clientes</span>
                    </div>
                    <span className="text-xl font-bold text-[#4A7C59]">{dailyReport.new_clients}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="text-[#C25953]" size={16} />
                      <span className="text-sm">Cancelaciones</span>
                    </div>
                    <span className="text-xl font-bold text-[#C25953]">{dailyReport.cancellations}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div className="flex items-center gap-2">
                      <CreditCard className="text-[#4A7C59]" size={16} />
                      <span className="text-sm">Pagos recuperados</span>
                    </div>
                    <span className="text-xl font-bold text-[#4A7C59]">{dailyReport.recovered_payments}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-[#F5F0E8] rounded-lg">
                <p className="text-xs text-[#5C7083]">Responsable</p>
                <p className="font-medium text-[#1A2835]">{dailyReport.responsible}</p>
              </div>
              {dailyReport.observations && (
                <div className="p-3 bg-[#F5F0E8] rounded-lg col-span-3">
                  <p className="text-xs text-[#5C7083]">Observaciones</p>
                  <p className="font-medium text-[#1A2835]">{dailyReport.observations}</p>
                </div>
              )}
            </div>

            {/* Sales by Plan */}
            {dailyReport.sales_by_plan && Object.keys(dailyReport.sales_by_plan).length > 0 && (
              <div className="p-4 bg-[#F5F0E8] rounded-lg">
                <h5 className="font-semibold text-[#1A2835] mb-3">Ventas por Plan</h5>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(dailyReport.sales_by_plan).map(([plan, count]) => (
                    <div key={plan} className="flex justify-between p-2 bg-white rounded">
                      <span className="text-sm text-[#5C7083]">{plan}</span>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            </>
            )}
          </div>
        )}

        {dailyReport?.message && (
          <div className="p-4 bg-[#F5F0E8] rounded-lg text-center text-[#5C7083]" data-testid="no-report-data">
            {dailyReport.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reportes;
